<template>
  <div>
    <b-card>
      <b-row class="mb-3">
        <b-col>
          <h3 class="mb-0">Data Cleanup</h3>
          <p class="text-muted">Remove inactive teachers, students, and unused courses</p>
        </b-col>
      </b-row>

      <b-alert show variant="danger" class="mb-4">
        <feather type="alert-triangle" class="feather-sm mr-1"></feather>
        <strong>These deletions are permanent and cannot be undone.</strong>
        Deleting a teacher or course also removes its chat messages, registrations, and payment records.
        Review each list carefully and uncheck anything you want to keep before deleting.
      </b-alert>

      <b-tabs content-class="mt-3">
        <!-- ================= Teachers ================= -->
        <b-tab title="Inactive Teachers" active>
          <b-row class="mb-3 align-items-center">
            <b-col>
              <p class="text-muted mb-0">
                Teachers with no course that has ever had a registration (including teachers with zero courses).
              </p>
            </b-col>
            <b-col class="text-right">
              <b-button variant="outline-primary" size="sm" @click="loadTeachers" :disabled="loadingTeachers">
                <feather type="refresh-cw" class="feather-sm mr-1"></feather>
                Refresh
              </b-button>
            </b-col>
          </b-row>

          <b-table
            :items="teachers"
            :fields="teacherFields"
            :busy="loadingTeachers"
            striped
            hover
            responsive
            show-empty
            empty-text="No inactive teachers found."
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Loading teachers...</strong>
              </div>
            </template>

            <template #head(select)>
              <b-form-checkbox
                :checked="teachers.length > 0 && selectedTeacherIds.length === teachers.length"
                @change="toggleAllTeachers"
              ></b-form-checkbox>
            </template>
            <template #cell(select)="data">
              <b-form-checkbox v-model="selectedTeacherIds" :value="data.item.user_id"></b-form-checkbox>
            </template>

            <template #cell(name)="data">
              <div class="font-weight-bold">{{ displayName(data.item) }}</div>
              <small class="text-muted">{{ data.item.email }}</small>
            </template>

            <template #cell(course_count)="data">
              <b-badge variant="secondary">{{ data.value }} course(s)</b-badge>
            </template>

            <template #cell(created_on)="data">
              {{ formatDate(data.value) }}
            </template>
          </b-table>

          <div class="text-right mt-3">
            <b-button
              variant="danger"
              :disabled="selectedTeacherIds.length === 0 || deletingTeachers"
              @click="deleteSelectedTeachers"
            >
              <feather type="trash-2" class="feather-sm mr-1"></feather>
              Delete Selected ({{ selectedTeacherIds.length }})
            </b-button>
          </div>
        </b-tab>

        <!-- ================= Courses ================= -->
        <b-tab title="Unused / Expired Courses">
          <b-row class="mb-3 align-items-center">
            <b-col>
              <p class="text-muted mb-0">
                Courses with no registrations, or whose end date has already passed.
              </p>
            </b-col>
            <b-col class="text-right">
              <b-button variant="outline-primary" size="sm" @click="loadCourses" :disabled="loadingCourses">
                <feather type="refresh-cw" class="feather-sm mr-1"></feather>
                Refresh
              </b-button>
            </b-col>
          </b-row>

          <b-table
            :items="courses"
            :fields="courseFields"
            :busy="loadingCourses"
            striped
            hover
            responsive
            show-empty
            empty-text="No courses match the cleanup criteria."
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Loading courses...</strong>
              </div>
            </template>

            <template #head(select)>
              <b-form-checkbox
                :checked="courses.length > 0 && selectedCourseIds.length === courses.length"
                @change="toggleAllCourses"
              ></b-form-checkbox>
            </template>
            <template #cell(select)="data">
              <b-form-checkbox v-model="selectedCourseIds" :value="data.item.course_id"></b-form-checkbox>
            </template>

            <template #cell(title)="data">
              <div class="font-weight-bold">{{ data.value }}</div>
              <small class="text-muted">Teacher: {{ data.item.teacher_name || 'N/A' }}</small>
            </template>

            <template #cell(reason)="data">
              <b-badge v-if="Number(data.item.registration_count) === 0" variant="warning" class="mr-1">
                No Registrations
              </b-badge>
              <b-badge v-if="data.item.expired" variant="secondary">
                Expired
              </b-badge>
            </template>

            <template #cell(registration_count)="data">
              {{ data.value }}
            </template>

            <template #cell(fee)="data">
              PKR {{ Number(data.value || 0).toLocaleString() }}
            </template>

            <template #cell(end_date)="data">
              {{ formatDate(data.value) }}
            </template>
          </b-table>

          <div class="text-right mt-3">
            <b-button
              variant="danger"
              :disabled="selectedCourseIds.length === 0 || deletingCourses"
              @click="deleteSelectedCourses"
            >
              <feather type="trash-2" class="feather-sm mr-1"></feather>
              Delete Selected ({{ selectedCourseIds.length }})
            </b-button>
          </div>
        </b-tab>

        <!-- ================= Students ================= -->
        <b-tab title="Inactive Students">
          <b-row class="mb-3 align-items-center">
            <b-col>
              <p class="text-muted mb-0">
                Students with no currently active (paid) course registration.
              </p>
            </b-col>
            <b-col class="text-right">
              <b-button variant="outline-primary" size="sm" @click="loadStudents" :disabled="loadingStudents">
                <feather type="refresh-cw" class="feather-sm mr-1"></feather>
                Refresh
              </b-button>
            </b-col>
          </b-row>

          <b-table
            :items="students"
            :fields="studentFields"
            :busy="loadingStudents"
            striped
            hover
            responsive
            show-empty
            empty-text="No inactive students found."
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Loading students...</strong>
              </div>
            </template>

            <template #head(select)>
              <b-form-checkbox
                :checked="students.length > 0 && selectedStudentIds.length === students.length"
                @change="toggleAllStudents"
              ></b-form-checkbox>
            </template>
            <template #cell(select)="data">
              <b-form-checkbox v-model="selectedStudentIds" :value="data.item.user_id"></b-form-checkbox>
            </template>

            <template #cell(name)="data">
              <div class="font-weight-bold">{{ displayName(data.item) }}</div>
              <small class="text-muted">{{ data.item.email }}</small>
            </template>

            <template #cell(created_on)="data">
              {{ formatDate(data.value) }}
            </template>
          </b-table>

          <div class="text-right mt-3">
            <b-button
              variant="danger"
              :disabled="selectedStudentIds.length === 0 || deletingStudents"
              @click="deleteSelectedStudents"
            >
              <feather type="trash-2" class="feather-sm mr-1"></feather>
              Delete Selected ({{ selectedStudentIds.length }})
            </b-button>
          </div>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import CleanupService from '@/services/cleanup.service';
import moment from 'moment';

export default {
  name: 'CleanupManagement',
  data() {
    return {
      teachers: [],
      selectedTeacherIds: [],
      loadingTeachers: false,
      deletingTeachers: false,
      teacherFields: [
        { key: 'select', label: '' },
        { key: 'name', label: 'Teacher' },
        { key: 'phone', label: 'Phone' },
        { key: 'course_count', label: 'Courses' },
        { key: 'created_on', label: 'Joined' },
      ],

      courses: [],
      selectedCourseIds: [],
      loadingCourses: false,
      deletingCourses: false,
      courseFields: [
        { key: 'select', label: '' },
        { key: 'title', label: 'Course' },
        { key: 'reason', label: 'Reason' },
        { key: 'registration_count', label: 'Registrations' },
        { key: 'fee', label: 'Fee' },
        { key: 'end_date', label: 'End Date' },
      ],

      students: [],
      selectedStudentIds: [],
      loadingStudents: false,
      deletingStudents: false,
      studentFields: [
        { key: 'select', label: '' },
        { key: 'name', label: 'Student' },
        { key: 'phone', label: 'Phone' },
        { key: 'created_on', label: 'Joined' },
      ],
    };
  },
  created() {
    this.loadTeachers();
    this.loadCourses();
    this.loadStudents();
  },
  methods: {
    displayName(user) {
      const profile = user?.profile || {};
      const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
      return name || user?.email || 'User';
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('MMM DD, YYYY');
    },

    // ---- Teachers ----
    async loadTeachers() {
      this.loadingTeachers = true;
      this.selectedTeacherIds = [];
      try {
        const response = await CleanupService.getTeachersWithoutRegistrations();
        this.teachers = response.data || [];
      } catch (error) {
        this.$bvToast.toast('Failed to load teachers', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loadingTeachers = false;
      }
    },
    toggleAllTeachers(checked) {
      this.selectedTeacherIds = checked ? this.teachers.map(t => t.user_id) : [];
    },
    async deleteSelectedTeachers() {
      const count = this.selectedTeacherIds.length;
      if (count === 0) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Permanently delete ${count} teacher(s) along with all of their courses, chat messages, registrations, and payment records? This cannot be undone.`,
        { title: 'Confirm Deletion', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel' }
      );
      if (!confirmed) return;

      this.deletingTeachers = true;
      try {
        await CleanupService.deleteTeachers(this.selectedTeacherIds);
        this.$bvToast.toast(`${count} teacher(s) deleted successfully`, { title: 'Success', variant: 'success', solid: true });
        this.loadTeachers();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to delete teachers', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.deletingTeachers = false;
      }
    },

    // ---- Courses ----
    async loadCourses() {
      this.loadingCourses = true;
      this.selectedCourseIds = [];
      try {
        const response = await CleanupService.getCoursesEligibleForDeletion();
        this.courses = response.data || [];
      } catch (error) {
        this.$bvToast.toast('Failed to load courses', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loadingCourses = false;
      }
    },
    toggleAllCourses(checked) {
      this.selectedCourseIds = checked ? this.courses.map(c => c.course_id) : [];
    },
    async deleteSelectedCourses() {
      const count = this.selectedCourseIds.length;
      if (count === 0) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Permanently delete ${count} course(s) along with their chat messages, registrations, and payment records? This cannot be undone.`,
        { title: 'Confirm Deletion', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel' }
      );
      if (!confirmed) return;

      this.deletingCourses = true;
      try {
        await CleanupService.deleteCourses(this.selectedCourseIds);
        this.$bvToast.toast(`${count} course(s) deleted successfully`, { title: 'Success', variant: 'success', solid: true });
        this.loadCourses();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to delete courses', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.deletingCourses = false;
      }
    },

    // ---- Students ----
    async loadStudents() {
      this.loadingStudents = true;
      this.selectedStudentIds = [];
      try {
        const response = await CleanupService.getStudentsWithoutActiveCourse();
        this.students = response.data || [];
      } catch (error) {
        this.$bvToast.toast('Failed to load students', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loadingStudents = false;
      }
    },
    toggleAllStudents(checked) {
      this.selectedStudentIds = checked ? this.students.map(s => s.user_id) : [];
    },
    async deleteSelectedStudents() {
      const count = this.selectedStudentIds.length;
      if (count === 0) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Permanently delete ${count} student(s) along with their registrations, chat messages, and payment records? This cannot be undone.`,
        { title: 'Confirm Deletion', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel' }
      );
      if (!confirmed) return;

      this.deletingStudents = true;
      try {
        await CleanupService.deleteStudents(this.selectedStudentIds);
        this.$bvToast.toast(`${count} student(s) deleted successfully`, { title: 'Success', variant: 'success', solid: true });
        this.loadStudents();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to delete students', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.deletingStudents = false;
      }
    },
  },
};
</script>

<style scoped>
.feather-sm {
  width: 16px;
  height: 16px;
}
</style>
